// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var _a;
// Mirror of sequent-core's `signing` module and of the Harvest signing API.
// Enum values are the kebab-case wire values the server sends and accepts.
import { IPermissions } from "@/types/keycloak";
/** The catalog of protected actions. Each value is an id; its labels are under `signing.actions.<id>`. */
export var SigningAction;
(function (SigningAction) {
    SigningAction["InitializeVoting"] = "initialize-voting";
    SigningAction["OpenVoting"] = "open-voting";
    SigningAction["CloseVoting"] = "close-voting";
    SigningAction["GenerateElectionReturns"] = "generate-election-returns";
    SigningAction["GenerateReports"] = "generate-reports";
    SigningAction["TransmitResults"] = "transmit-results";
    SigningAction["ApproveVoter"] = "approve-voter";
    SigningAction["ApproveConfiguration"] = "approve-configuration";
    SigningAction["ConfirmCustodyProof"] = "key-ceremony";
    SigningAction["ContributeCustodyProof"] = "tally-key";
})(SigningAction || (SigningAction = {}));
/** What a request is bound to. Post = election, country = area. */
export var SigningScope;
(function (SigningScope) {
    SigningScope["Post"] = "post";
    SigningScope["PostAndCountry"] = "post-and-country";
    SigningScope["Event"] = "event";
    SigningScope["Trustee"] = "trustee";
})(SigningScope || (SigningScope = {}));
export var ExecutionMode;
(function (ExecutionMode) {
    ExecutionMode["Deferred"] = "deferred";
    ExecutionMode["Gate"] = "gate";
})(ExecutionMode || (ExecutionMode = {}));
export var DocumentKind;
(function (DocumentKind) {
    DocumentKind["NoDocument"] = "no-document";
    DocumentKind["Pdf"] = "pdf";
    DocumentKind["Eml"] = "eml";
})(DocumentKind || (DocumentKind = {}));
/** Labelled by `signing.groups.<value>`. */
export var SigningActionGroup;
(function (SigningActionGroup) {
    SigningActionGroup["Voting"] = "voting";
    SigningActionGroup["ResultsAndReports"] = "results-and-reports";
    SigningActionGroup["Enrollment"] = "enrollment";
    SigningActionGroup["ConfigurationAndKeys"] = "configuration-and-keys";
})(SigningActionGroup || (SigningActionGroup = {}));
export var SigningRequirement;
(function (SigningRequirement) {
    SigningRequirement["NotRequired"] = "not-required";
    SigningRequirement["Required"] = "required";
})(SigningRequirement || (SigningRequirement = {}));
export var RequesterSigning;
(function (RequesterSigning) {
    RequesterSigning["Allowed"] = "allowed";
    RequesterSigning["NotAllowed"] = "not-allowed";
})(RequesterSigning || (RequesterSigning = {}));
/** Labelled by `signing.status.<value>`. */
export var SigningRequestStatus;
(function (SigningRequestStatus) {
    SigningRequestStatus["Waiting"] = "waiting";
    SigningRequestStatus["Completed"] = "completed";
    SigningRequestStatus["Executed"] = "executed";
    SigningRequestStatus["Cancelled"] = "cancelled";
    SigningRequestStatus["Expired"] = "expired";
    SigningRequestStatus["Failed"] = "failed";
})(SigningRequestStatus || (SigningRequestStatus = {}));
/** Labelled by `signing.cancelReasons.<value>`. */
export var CancelReason;
(function (CancelReason) {
    CancelReason["ByRequester"] = "by-requester";
    CancelReason["ByOperator"] = "by-operator";
    CancelReason["RuleChanged"] = "rule-changed";
    CancelReason["PayloadChanged"] = "payload-changed";
    CancelReason["Superseded"] = "superseded";
    CancelReason["CertificateRevoked"] = "certificate-revoked";
})(CancelReason || (CancelReason = {}));
export var RevocationCheck;
(function (RevocationCheck) {
    RevocationCheck["Check"] = "check";
    RevocationCheck["DontCheck"] = "dont-check";
})(RevocationCheck || (RevocationCheck = {}));
export var CrlUnavailablePolicy;
(function (CrlUnavailablePolicy) {
    CrlUnavailablePolicy["Refuse"] = "refuse";
    CrlUnavailablePolicy["AcceptUnchecked"] = "accept-unchecked";
})(CrlUnavailablePolicy || (CrlUnavailablePolicy = {}));
export var CertificateRegistration;
(function (CertificateRegistration) {
    CertificateRegistration["OnFirstUse"] = "on-first-use";
    CertificateRegistration["SecurityOfficerOnly"] = "security-officer-only";
})(CertificateRegistration || (CertificateRegistration = {}));
export var CertificatePostBinding;
(function (CertificatePostBinding) {
    CertificatePostBinding["OnePost"] = "one-post";
    CertificatePostBinding["AnyPost"] = "any-post";
})(CertificatePostBinding || (CertificatePostBinding = {}));
export var StaffCertificateStatus;
(function (StaffCertificateStatus) {
    StaffCertificateStatus["Active"] = "active";
    StaffCertificateStatus["Revoked"] = "revoked";
})(StaffCertificateStatus || (StaffCertificateStatus = {}));
/** How a staff certificate was registered. */
export var StaffCertificateRegistration;
(function (StaffCertificateRegistration) {
    StaffCertificateRegistration["FirstUse"] = "first-use";
    StaffCertificateRegistration["SecurityOfficer"] = "security-officer";
})(StaffCertificateRegistration || (StaffCertificateRegistration = {}));
export var CertificateAuthorityPurpose;
(function (CertificateAuthorityPurpose) {
    CertificateAuthorityPurpose["VoterSignIn"] = "voter-sign-in";
    CertificateAuthorityPurpose["StaffSignatures"] = "staff-signatures";
})(CertificateAuthorityPurpose || (CertificateAuthorityPurpose = {}));
export var SignatureAlgorithm;
(function (SignatureAlgorithm) {
    SignatureAlgorithm["RsaPkcs1Sha256"] = "rsa-pkcs1-sha256";
    SignatureAlgorithm["EcdsaP256Sha256"] = "ecdsa-p256-sha256";
})(SignatureAlgorithm || (SignatureAlgorithm = {}));
export var RevocationStatus;
(function (RevocationStatus) {
    RevocationStatus["Checked"] = "checked";
    RevocationStatus["Unchecked"] = "unchecked";
})(RevocationStatus || (RevocationStatus = {}));
export var CrlStatus;
(function (CrlStatus) {
    CrlStatus["Ok"] = "ok";
    CrlStatus["Unavailable"] = "unavailable";
})(CrlStatus || (CrlStatus = {}));
/** Why a certificate file didn't open; logged without the file or its password. */
export var CertificateOpenFailure;
(function (CertificateOpenFailure) {
    CertificateOpenFailure["WrongPassword"] = "wrong-password";
    CertificateOpenFailure["Unreadable"] = "unreadable";
    CertificateOpenFailure["NoKey"] = "no-key";
})(CertificateOpenFailure || (CertificateOpenFailure = {}));
/** The server's certificate checks, in dialog order. Labelled by `signing.dialog.checks.passed.<value>` and `signing.dialog.checks.failed.<value>`. */
export var CertificateCheckId;
(function (CertificateCheckId) {
    CertificateCheckId["TrustedIssuer"] = "trusted-issuer";
    CertificateCheckId["ValidNow"] = "valid-now";
    CertificateCheckId["SigningKeyUsage"] = "signing-key-usage";
    CertificateCheckId["NotRevoked"] = "not-revoked";
    CertificateCheckId["Registered"] = "registered";
    CertificateCheckId["RegisteredToOther"] = "registered-to-other";
    CertificateCheckId["AlreadySigned"] = "already-signed";
    CertificateCheckId["PostBinding"] = "post-binding";
    CertificateCheckId["Signature"] = "signature";
})(CertificateCheckId || (CertificateCheckId = {}));
/** The same mapping as `SigningAction`'s helpers in sequent-core, in catalog order. */
export var SIGNING_ACTIONS = (_a = {},
    _a[SigningAction.InitializeVoting] = {
        group: SigningActionGroup.Voting,
        scope: SigningScope.PostAndCountry,
        mode: ExecutionMode.Deferred,
        document: DocumentKind.NoDocument,
        signPermission: IPermissions.SIGN_INITIALIZE_VOTING,
    },
    _a[SigningAction.OpenVoting] = {
        group: SigningActionGroup.Voting,
        scope: SigningScope.Post,
        mode: ExecutionMode.Deferred,
        document: DocumentKind.NoDocument,
        signPermission: IPermissions.SIGN_OPEN_VOTING,
    },
    _a[SigningAction.CloseVoting] = {
        group: SigningActionGroup.Voting,
        scope: SigningScope.Post,
        mode: ExecutionMode.Deferred,
        document: DocumentKind.NoDocument,
        signPermission: IPermissions.SIGN_CLOSE_VOTING,
    },
    _a[SigningAction.GenerateElectionReturns] = {
        group: SigningActionGroup.ResultsAndReports,
        scope: SigningScope.PostAndCountry,
        mode: ExecutionMode.Deferred,
        document: DocumentKind.Pdf,
        signPermission: IPermissions.SIGN_GENERATE_ELECTION_RETURNS,
    },
    _a[SigningAction.GenerateReports] = {
        group: SigningActionGroup.ResultsAndReports,
        scope: SigningScope.Post,
        mode: ExecutionMode.Deferred,
        document: DocumentKind.Pdf,
        signPermission: IPermissions.SIGN_GENERATE_REPORTS,
    },
    _a[SigningAction.TransmitResults] = {
        group: SigningActionGroup.ResultsAndReports,
        scope: SigningScope.PostAndCountry,
        mode: ExecutionMode.Deferred,
        document: DocumentKind.Eml,
        signPermission: IPermissions.SIGN_TRANSMIT_RESULTS,
    },
    _a[SigningAction.ApproveVoter] = {
        group: SigningActionGroup.Enrollment,
        scope: SigningScope.Post,
        mode: ExecutionMode.Deferred,
        document: DocumentKind.NoDocument,
        signPermission: IPermissions.SIGN_APPROVE_VOTER,
    },
    _a[SigningAction.ApproveConfiguration] = {
        group: SigningActionGroup.ConfigurationAndKeys,
        scope: SigningScope.Event,
        mode: ExecutionMode.Deferred,
        document: DocumentKind.NoDocument,
        signPermission: IPermissions.SIGN_APPROVE_CONFIGURATION,
    },
    _a[SigningAction.ConfirmCustodyProof] = {
        group: SigningActionGroup.ConfigurationAndKeys,
        scope: SigningScope.Trustee,
        mode: ExecutionMode.Gate,
        document: DocumentKind.NoDocument,
        signPermission: IPermissions.SIGN_KEY_CEREMONY,
    },
    _a[SigningAction.ContributeCustodyProof] = {
        group: SigningActionGroup.ConfigurationAndKeys,
        scope: SigningScope.Trustee,
        mode: ExecutionMode.Gate,
        document: DocumentKind.NoDocument,
        signPermission: IPermissions.SIGN_TALLY_KEY,
    },
    _a);
/** The most signatures a rule may need (sequent-core `MAX_SIGNATURES`). */
export var MAX_SIGNATURES = 100;
/** The longest expiry a rule may set, in minutes: a year (sequent-core `MAX_EXPIRES_MINUTES`). */
export var MAX_EXPIRES_MINUTES = 525600;
/** The expiries the rule drawer offers, in minutes; `null` is no limit. */
export var SIGNING_EXPIRY_OPTIONS = [30, 60, 120, 1440, null];
/** A signer's status on a request. */
export var SigningSignerStatus;
(function (SigningSignerStatus) {
    SigningSignerStatus["Signed"] = "signed";
    SigningSignerStatus["NotSigned"] = "not-signed";
})(SigningSignerStatus || (SigningSignerStatus = {}));
/** What a saved rule should know about the Posts. */
export var SigningRuleWarning;
(function (SigningRuleWarning) {
    SigningRuleWarning["NoPosts"] = "no-posts";
    SigningRuleWarning["ShortPosts"] = "short-posts";
    SigningRuleWarning["RequesterExcluded"] = "requester-excluded";
})(SigningRuleWarning || (SigningRuleWarning = {}));
